import {useMutation} from "@apollo/client";
import {yupResolver} from "@hookform/resolvers/yup";
import classNames from "classnames";
import {useCallback} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {toast} from "react-hot-toast";

import {Card} from "@/components/card/Card.tsx";
import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {SwitchField} from "@/components/form/fields/SwitchField.tsx";
import {Section} from "@/components/section/Section.tsx";
import {GQL_STATUS_PAGE_QUERY, GQL_UPDATE_STATUS_PAGE_V2, UpdateStatusPageV2Result} from "@/lib/queries/statuspages.ts";
import {ResponseStatus, ServerErrors} from "@/lib/types/common.ts";
import {
  SubscribersSettingsFormDefaultValues,
  SubscribersSettingsFormSchema,
} from "@/pages/statuspages/tabs/subscribers/components/SubscribersSettingsForm/SubscribersSettingsForm.form.ts";
import {
  SubscribersSettingsFormKeys,
  SubscribersSettingsFormProps,
  SubscribersSettingsFormValues,
} from "@/pages/statuspages/tabs/subscribers/components/SubscribersSettingsForm/SubscribersSettingsForm.types.ts";

import styles from "@/pages/statuspages/tabs/subscribers/StatusPageSubscribersTab.module.scss";

export const SUBSCRIBERS_SETTINGS_FORM_ID = "subscribers-settings-form";

export const SubscribersSettingsForm = ({statusPage, formId, onSuccess}: SubscribersSettingsFormProps) => {
  const [updateStatusPage] = useMutation<UpdateStatusPageV2Result>(GQL_UPDATE_STATUS_PAGE_V2, {
    refetchQueries: [GQL_STATUS_PAGE_QUERY],
  });

  const methods = useForm<SubscribersSettingsFormValues>({
    resolver: yupResolver(SubscribersSettingsFormSchema),
    defaultValues: {
      ...SubscribersSettingsFormDefaultValues,
      allow_subscriptions_email: statusPage.allow_subscriptions_email,
      allow_subscriptions_rss: statusPage.allow_subscriptions_rss,
      allow_subscriptions_sms: statusPage.allow_subscriptions_sms,
      allow_subscriptions_webhook: statusPage.allow_subscriptions_webhook,
      allow_subscriptions_slack: statusPage.allow_subscriptions_slack,
      allow_notifications: statusPage.allow_notifications,
      email_from: statusPage.email_from || "",
      email_reply_to: statusPage.email_reply_to || "",
      contact_email: statusPage.contact_email || "",
    },
  });

  const handleSubmit = useCallback(
    async (formData: SubscribersSettingsFormValues) => {
      const response = await updateStatusPage({
        variables: {
          id: statusPage.id,
          data: formData,
        },
      });

      if (response?.data?.response?.status === ResponseStatus.SUCCESS) {
        methods.reset(formData);
        toast.success("Settings updated successfully");
        onSuccess?.();
      } else if (response?.data?.response?.status === ResponseStatus.ERROR) {
        const errorResponse = response.data.response as {status: ResponseStatus.ERROR; message: ServerErrors};
        const serverErrors = errorResponse.message;
        if (serverErrors) {
          Object.keys(serverErrors).forEach((field) => {
            if (field === "__all__") return;
            const fieldKey = field as SubscribersSettingsFormKeys;
            if (serverErrors[field]?.length) {
              methods.setError(fieldKey, {message: serverErrors[field][0]});
            }
          });
          if (serverErrors.__all__?.length) {
            toast.error(serverErrors.__all__[0]);
          }
        }
      }
    },
    [statusPage.id, updateStatusPage, methods, onSuccess]
  );

  return (
    <FormProvider {...methods}>
      <form
        id={formId || SUBSCRIBERS_SETTINGS_FORM_ID}
        onSubmit={methods.handleSubmit(handleSubmit)}
        className={classNames("d-flex flex-column gap-4", styles.subscriptionChannels)}
      >
        <Section
          title={"SUBSCRIPTION CHANNELS"}
          description={"Choose which subscription methods are available to your page visitors"}
        />
        <div className={styles.channelGrid}>
          <Card variant={"white"} className={"p-3"}>
            <SwitchField
              name={"allow_subscriptions_email"}
              label={"Email"}
              labelPosition={"left"}
              labelTypography={"bodySmallMedium"}
            />
          </Card>
          <Card variant={"white"} className={"p-3"}>
            <SwitchField
              name={"allow_subscriptions_rss"}
              label={"RSS"}
              labelPosition={"left"}
              labelTypography={"bodySmallMedium"}
            />
          </Card>
          <Card variant={"white"} className={"p-3"}>
            <SwitchField
              name={"allow_subscriptions_sms"}
              label={"SMS"}
              labelPosition={"left"}
              labelTypography={"bodySmallMedium"}
            />
          </Card>
          <Card variant={"white"} className={"p-3"}>
            <SwitchField
              name={"allow_subscriptions_webhook"}
              label={"Webhook"}
              labelPosition={"left"}
              labelTypography={"bodySmallMedium"}
            />
          </Card>
          <Card variant={"white"} className={"p-3"}>
            <SwitchField
              name={"allow_subscriptions_slack"}
              label={"Slack"}
              labelPosition={"left"}
              labelTypography={"bodySmallMedium"}
            />
          </Card>
        </div>

        <Card variant={"white"}>
          <SwitchField
            name={"allow_notifications"}
            label={"Enable sending notifications to subscribers"}
            description={"Any subscribers, even if channel subscriptions are disabled, will be sent notifications"}
            labelPosition={"left"}
          />
        </Card>

        <Section title={"EMAIL SETTINGS"} description={"Custom sender address, reply-to, and contact email"} />
        <div className={"d-flex flex-column gap-4"}>
          <InputField
            name={"email_from"}
            label={"Send from email"}
            tooltip={"If empty, notifications are sent from the default Uptime.com email address."}
            helpText={"This will require DNS changes for your domain (e.g. SPF and/or DKIM records)."}
            supportLink={
              "https://support.uptime.com/hc/en-us/articles/360016295019-Create-an-SPF-Record-to-Send-Email-via-Uptime-com"
            }
          />
          <InputField name={"email_reply_to"} label={"Reply to email"} />
          <InputField
            name={"contact_email"}
            label={"Contact Email Address"}
            helpText={"Allow page visitors to report issues to this email address (e.g. support@your-domain.com)."}
          />
        </div>
      </form>
    </FormProvider>
  );
};
